"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Clock, Layers, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ContractorsContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const roofingFeatures = ["Fast turnaround for urgent re-roofs and weather-critical repairs", "Integrated triple handrail and toe-board edge protection", "Reinforced hoist and tile loading platforms built to spec", "Chimney access towers and multi-elevation saddle stages"];
  const solarFeatures = ["Flexible handover schedules synced to your installation crews", "Generous panel lifting clearances and unobstructed roof lines", "Gable-end and eaves access points for inverter cable runs", "Prompt dismantle on completion to minimise customer disruption"];
  const columnVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Resolve Roofing Image
  const roofingPhoto = posts?.find(p => p.customFields?.photo_name === "Homepage Roofing Contractor Image" || (p as any)?.acf?.photo_name === "Homepage Roofing Contractor Image" || p.title === "Homepage Roofing Contractor Image");
  const roofingCustomImg = roofingPhoto?.customFields?.image || (roofingPhoto as any)?.acf?.image || roofingPhoto?.featuredImage;
  const roofingImage = roofingCustomImg && typeof roofingCustomImg === "string" ? {
    src: roofingCustomImg,
    srcset: "",
    sizes: "",
    alt: roofingPhoto?.customFields?.alt_text || (roofingPhoto as any)?.acf?.alt_text || roofingPhoto?.title || "Perimeter roofing scaffolding and safe work platforms installed for tile loading and roof repairs"
  } : {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofer.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofer.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofer.png-wvc-sizes",
    alt: "Perimeter roofing scaffolding and safe work platforms installed for tile loading and roof repairs"
  };

  // Resolve Solar Image
  const solarPhoto = posts?.find(p => p.customFields?.photo_name === "Homepage Solar Contractor Image" || (p as any)?.acf?.photo_name === "Homepage Solar Contractor Image" || p.title === "Homepage Solar Contractor Image");
  const solarCustomImg = solarPhoto?.customFields?.image || (solarPhoto as any)?.acf?.image || solarPhoto?.featuredImage;
  const solarImage = solarCustomImg && typeof solarCustomImg === "string" ? {
    src: solarCustomImg,
    srcset: "",
    sizes: "",
    alt: solarPhoto?.customFields?.alt_text || (solarPhoto as any)?.acf?.alt_text || solarPhoto?.title || "Solar PV access scaffolding with generous panel lifting clearances and unobstructed roof lines"
  } : {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel.png-wvc-sizes",
    alt: "Solar PV access scaffolding with generous panel lifting clearances and unobstructed roof lines"
  };
  return <section id="roofing-solar-contractor-positioning" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-primary" />
            <span className="text-xs md:text-sm font-bold tracking-wider uppercase text-muted-foreground">
              Specialist Trade Access
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Tailored access for roofers and solar specialists.
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Keep your installations on schedule with scaffolding configured specifically for tile loads, panel clearances, and strict site safety standards across the South East. We aim to become the scaffolding partner roofing and solar contractors can rely on job after job, not just a one-off supplier.
          </p>
        </div>

        {/* Dual-Column Capability Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Left Column: Roofing Contractors */}
          <motion.div variants={columnVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }}>
            <Card className="h-full border border-border bg-card p-0 overflow-hidden flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
              <div>
                {/* Visual Header / Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={roofingImage.src} data-wvc-srcset={roofingImage.srcset} data-wvc-sizes={roofingImage.sizes} alt={roofingImage.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground text-xs font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1.5 shadow-sm">
                    <Layers className="size-3.5 text-primary" />
                    <span>Roofing Contractors</span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="p-2 rounded-md bg-accent text-accent-foreground">
                      <Clock className="size-5 text-primary" />
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold text-card-foreground">
                      Roofing access &amp; loading bays
                    </h3>
                  </div>

                  <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
                    Designed around your workflow. We erect robust perimeter scaffolds that give roofers safe, unhindered access to eaves, valleys, and ridges with zero wasted platform space.
                  </p>

                  {/* Feature Checklist */}
                  <ul className="space-y-3 mb-8">
                    {roofingFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-card-foreground font-medium">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Card Footer Action */}
              <div className="px-6 pb-6 md:px-8 md:pb-8 pt-0 mt-auto">
                <Button asChild className="w-full h-12 text-sm font-semibold justify-between bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors">
                  <Link to="/RoofingScaffoldingPage">
                    <span>Explore roofing scaffolding</span>
                    <ArrowRight className="size-4 text-primary" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Right Column: Solar PV Installers */}
          <motion.div variants={columnVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }}>
            <Card className="h-full border border-border bg-card p-0 overflow-hidden flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
              <div>
                {/* Visual Header / Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={solarImage.src} data-wvc-srcset={solarImage.srcset} data-wvc-sizes={solarImage.sizes} alt={solarImage.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground text-xs font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1.5 shadow-sm">
                    <Zap className="size-3.5 text-primary" />
                    <span>Solar PV Installers</span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="p-2 rounded-md bg-accent text-accent-foreground">
                      <ShieldCheck className="size-5 text-primary" />
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold text-card-foreground">
                      Solar installation scaffolding
                    </h3>
                  </div>

                  <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
                    Tailored setups for renewable energy teams. We provide reliable handover dates and clearance zones so panel delivery, wiring, and fixing proceed without site hold-ups.
                  </p>

                  {/* Feature Checklist */}
                  <ul className="space-y-3 mb-8">
                    {solarFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-card-foreground font-medium">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Card Footer Action */}
              <div className="px-6 pb-6 md:px-8 md:pb-8 pt-0 mt-auto">
                <Button asChild className="w-full h-12 text-sm font-semibold justify-between bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors">
                  <Link to="/SolarScaffoldingPage">
                    <span>Explore solar scaffolding</span>
                    <ArrowRight className="size-4 text-primary" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Shared Accreditation Strip */}
        <div className="mt-12 p-5 md:p-6 rounded-md bg-accent border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <ShieldCheck className="size-6 text-primary shrink-0" />
            <div>
              <p className="text-sm font-bold text-foreground">
                Verified trade standards on every handover
              </p>
              <p className="text-xs text-muted-foreground">
                Compliant with NASC and CHAS protocols across all sites.
              </p>
            </div>
          </div>
          <Button asChild variant="default" className="w-full sm:w-auto shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactRequestQuotePage">
              <span>Book contractor access</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}
export default function Contractors() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ContractorsContent />
    </PostsProvider>;
}